import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import {
  useTanstackRewardsFetchFn,
  useTanstackUserRewardsFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { useNotificationBadge } from '@/components/Functional/hooks/useNotificationBadge'
import { mergeRewardsWithUserData } from '@/components/Functional/lib/api/rewards'
import { buildDisplayRewards } from '@/components/Functional/utils/rewardGrouping'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { RaundLifeCycleDebug } from '@/components/Layout/debug/RaundLifeCycleDebug'
import { type FilterTab, RewardFilterTabs } from '@/components/Pages/rewards/components/list/RewardFilterTabs'
import { RewardList } from '@/components/Pages/rewards/components/list/RewardList'
import { RewardListSkeleton } from '@/components/Pages/rewards/components/list/RewardListSkeleton'
import type { RewardOverviewData } from '@/components/Pages/rewards/components/list/RewardOverview'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from '@xstate/react'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'

// TODO(#2319): FilterTab の内部識別子を日本語文字列から 'all' | 'achieved' | ... などの安定したキーに変更して
//              i18n 対応時にフィルタロジックが壊れないようにする
type DisplayLabel = '未達成' | '達成' | '完了' | '終了'

const DISPLAY_LABEL_MAP: Record<
  | 'UNOBTAINED'
  | 'ACHIEVABLE'
  | 'ACHIEVED'
  | 'EXCHANGED'
  | 'USED'
  | 'UNAPPLIED'
  | 'APPLIED'
  | 'LOST'
  | 'ENDED'
  | 'SOLD_OUT',
  DisplayLabel
> = {
  UNOBTAINED: '未達成',
  ACHIEVABLE: '達成',
  ACHIEVED: '達成',
  EXCHANGED: '完了',
  USED: '完了',
  UNAPPLIED: '達成',
  APPLIED: '完了',
  LOST: '完了',
  ENDED: '終了',
  SOLD_OUT: '終了',
}

export const RewardsListPage = () => {
  const router = useRouter()
  const { gen } = useCampaignSlug()

  const appActorRef = AppMachineContext.useActorRef()
  const isAuthenticated = useSelector(appActorRef, (s) => s.context.isAuthenticated)
  const { hasUnread } = useNotificationBadge(isAuthenticated)

  const rewardsQuery = useTanstackRewardsFetchFn()
  const userRewardsQuery = useTanstackUserRewardsFetchFn()
  const { data: rewardsData, isLoading: isRewardsLoading } = useQuery(rewardsQuery)
  const { data: userRewardsData, isLoading: isUserRewardsLoading } = useQuery({
    ...userRewardsQuery,
    enabled: isAuthenticated,
  })
  const isLoading = isRewardsLoading || (isAuthenticated && isUserRewardsLoading)
  const data = useMemo(
    () =>
      mergeRewardsWithUserData(
        rewardsData ?? { rewards: [] },
        isAuthenticated ? (userRewardsData ?? { rewards: [] }) : { rewards: [] },
      ),
    [rewardsData, userRewardsData, isAuthenticated],
  )

  const [activeFilter, setActiveFilter] = useState<FilterTab>('すべて')

  const displayRewards: RewardOverviewData[] = useMemo(() => buildDisplayRewards(data.rewards), [data])

  const filteredRewards = useMemo(() => {
    if (activeFilter === 'すべて') return displayRewards
    return displayRewards.filter(
      (r) => r.acquisitionStatus != null && DISPLAY_LABEL_MAP[r.acquisitionStatus] === activeFilter,
    )
  }, [displayRewards, activeFilter])

  return (
    <>
      <Header title="特典" onNotification={() => router.push(gen('/news'))} hasUnreadBadge={hasUnread} />
      <main className="pb-[72px]">
        {isLoading ? (
          <RewardListSkeleton />
        ) : (
          <>
            <RewardFilterTabs active={activeFilter} onChange={setActiveFilter} />
            <RewardList rewards={filteredRewards} />
          </>
        )}
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>
      <BottomNavigation activeItem="reward" />
    </>
  )
}
